import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface Supplier {
  id?: number;
  supplierCode: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  status: 'Active' | 'Inactive' | 'Pending';
  totalOrders: number;
  totalValue: number;
  lastOrderDate?: string;
  paymentTerms: string;
  taxId?: string;
  website?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SupplierStats {
  totalSuppliers: number;
  activeSuppliers: number;
  inactiveSuppliers: number;
  pendingSuppliers: number;
  totalOrders: number;
  totalValue: number;
  averageOrderValue: number;
  topSuppliers: {
    name: string;
    orderCount: number;
    totalValue: number;
  }[];
  geographicalDistribution: {
    city: string;
    count: number;
  }[];
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  total?: number;
}

@Injectable({
  providedIn: 'root'
})
export class SuppliersService {
  private apiUrl = `${environment.apiServiceBaseUrl}/hawdco/suppliers`;

  constructor(private http: HttpClient) {}

  // Get all suppliers with optional pagination and filtering
  getSuppliers(
    page: number = 1, 
    limit: number = 10, 
    search?: string, 
    status?: string,
    city?: string
  ): Observable<ApiResponse<Supplier[]>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    
    if (search) params = params.set('search', search);
    if (status) params = params.set('status', status);
    if (city) params = params.set('city', city);

    return this.http.get<ApiResponse<Supplier[]>>(this.apiUrl, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get supplier by ID
  getSupplierById(id: number): Observable<ApiResponse<Supplier>> {
    return this.http.get<ApiResponse<Supplier>>(`${this.apiUrl}/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Create new supplier
  createSupplier(supplier: Supplier): Observable<ApiResponse<Supplier>> {
    return this.http.post<ApiResponse<Supplier>>(this.apiUrl, supplier)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Update supplier
  updateSupplier(id: number, supplier: Supplier): Observable<ApiResponse<Supplier>> {
    return this.http.put<ApiResponse<Supplier>>(`${this.apiUrl}/${id}`, supplier)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Delete supplier
  deleteSupplier(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Toggle supplier status
  toggleSupplierStatus(id: number, status: 'Active' | 'Inactive'): Observable<ApiResponse<Supplier>> {
    return this.http.put<ApiResponse<Supplier>>(`${this.apiUrl}/${id}/status`, { status })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get supplier statistics
  getSuppliersStats(): Observable<ApiResponse<SupplierStats>> {
    return this.http.get<ApiResponse<SupplierStats>>(`${this.apiUrl}/stats`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get supplier statuses
  getSupplierStatuses(): Observable<ApiResponse<string[]>> {
    return this.http.get<ApiResponse<string[]>>(`${this.apiUrl}/statuses`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get cities for dropdown
  getCities(): Observable<ApiResponse<string[]>> {
    return this.http.get<ApiResponse<string[]>>(`${this.apiUrl}/cities`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get supplier performance metrics
  getSupplierPerformance(id: number): Observable<ApiResponse<{
    averageDeliveryTime: number;
    onTimeDeliveryRate: number;
    qualityRating: number;
    totalOrders: number;
    totalValue: number;
    last12MonthsTrend: {
      month: string;
      orderCount: number;
      value: number;
    }[];
  }>> {
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/${id}/performance`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get supplier orders
  getSupplierOrders(id: number, limit: number = 10): Observable<ApiResponse<{
    orderNumber: string;
    orderDate: string;
    totalAmount: number;
    status: string;
  }[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.apiUrl}/${id}/orders?limit=${limit}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Validate supplier before creating/updating
  validateSupplier(supplier: Supplier): Observable<ApiResponse<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
    duplicateEmail?: boolean;
    duplicatePhone?: boolean;
  }>> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/validate`, supplier)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Generate supplier code
  generateSupplierCode(): Observable<ApiResponse<string>> {
    return this.http.get<ApiResponse<string>>(`${this.apiUrl}/generate-code`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Check if supplier code is unique
  checkSupplierCodeUnique(code: string, excludeId?: number): Observable<ApiResponse<boolean>> {
    let params = new HttpParams().set('code', code);
    if (excludeId) params = params.set('excludeId', excludeId.toString());

    return this.http.get<ApiResponse<boolean>>(`${this.apiUrl}/check-code`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Export suppliers to Excel
  exportToExcel(search?: string, status?: string, city?: string): Observable<Blob> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (status) params = params.set('status', status);
    if (city) params = params.set('city', city);

    return this.http.get(`${this.apiUrl}/export/excel`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Export suppliers to PDF
  exportToPDF(search?: string, status?: string, city?: string): Observable<Blob> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (status) params = params.set('status', status);
    if (city) params = params.set('city', city);

    return this.http.get(`${this.apiUrl}/export/pdf`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Bulk update supplier status
  bulkUpdateStatus(supplierIds: number[], status: 'Active' | 'Inactive'): Observable<ApiResponse<{
    updated: number;
    failed: {
      id: number;
      error: string;
    }[];
  }>> {
    return this.http.put<ApiResponse<any>>(`${this.apiUrl}/bulk-status`, {
      supplierIds,
      status
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Get supplier by code
  getSupplierByCode(supplierCode: string): Observable<ApiResponse<Supplier>> {
    return this.http.get<ApiResponse<Supplier>>(`${this.apiUrl}/code/${supplierCode}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Search suppliers by name or contact person
  searchSuppliers(query: string, limit: number = 20): Observable<ApiResponse<Supplier[]>> {
    return this.http.get<ApiResponse<Supplier[]>>(`${this.apiUrl}/search?q=${query}&limit=${limit}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Error handling
  private handleError(error: any): Observable<never> {
    let errorMessage = 'An error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
      
      if (error.error && error.error.message) {
        errorMessage = error.error.message;
      }
    }

    console.error('Suppliers API Error:', error);
    return throwError(() => new Error(errorMessage));
  }
}
